


import React, { ReactNode, useEffect, useState } from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "components/Select/Select";
import { InputIcon } from "primereact/inputicon";
import { Button } from "components/Button/Button";
import { Label } from "components/Label/Label";
import { Input } from "components/Input/Input";
import { Loader } from "components/Loader/Loader";
import { useLangContext } from "context/Lang";
import { useSelector } from "react-redux";
import { RootStateType } from "storeRedux/store";

import { motion } from "framer-motion";

type BaseInputConfig<T> = {
    name: keyof T;
    label?: string;
    placeholder?: string;
    className?: string;
    icon?: string;
    required?: boolean;
};

type DateInputConfig<T> = BaseInputConfig<T> & {
    type: "date";
};

type TextInputConfig<T> = BaseInputConfig<T> & {
    type: "text" | "email" | "password" | "number";
};

type SelectInputConfig<T> = BaseInputConfig<T> & {
    type: "select";
    options: { label: string; value: string | number }[];
};

type CheckboxInputConfig<T> = BaseInputConfig<T> & {
    type: "checkbox";
};

type FileInputConfig<T> = BaseInputConfig<T> & {
    type: "file";
    accept?: string;
};

export type InputConfig<T> =
    | TextInputConfig<T>
    | SelectInputConfig<T>
    | CheckboxInputConfig<T>
    | DateInputConfig<T>
    | FileInputConfig<T>

interface FormProps<T> {
    id?: string;
    inputs: InputConfig<T>[];
    defaultData?: T; // e.g. { firstname: "yusuf" }
    data?: T;
    onSubmit?: (data: T) => void;
    submitTitle?: string;
    inputsDisplayType?: "inline-block" | "flex";
    header?: ReactNode;
    footer?: ReactNode;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
    animate?: boolean
}

export function Form<T>({
    id,
    disabled,
    inputs,
    onSubmit,
    inputsDisplayType = "inline-block",
    className = " border rounded-lg p-4 border-primary-200 ",
    animate = false,
    ...props
}: FormProps<T>) {

    const { gW } = useLangContext();
    const config = useSelector((state: RootStateType) => state.config);
    const [submitted, setSubmitted] = useState(false);

    const { direction } = config

    const [formData, setFormData] = useState<Partial<T>>(
        props.defaultData || {}
    );

    useEffect(() => {
        if (props.data) {
            setFormData(props.data)
        }
    }, [props.data]);




    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, type, checked, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, files } = e.target;
        if (!files || !files[0]) return;

        setFormData((prev) => ({
            ...prev,
            [name]: files[0],
        }));
    };



    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);

        // Check required fields
        const hasError = inputs.some(input => {
            if (!input.required) return false;
            const value = formData[input.name];
            return value === undefined || value === "" || value === null || value === false;
        });

        if (hasError) return; // stop submission

        onSubmit?.(formData as T);
    };


    const getErrorClass = (input: InputConfig<T>) => {
        if (!submitted || !input.required) return "";
        const value = formData[input.name];

        const isEmpty =
            value === undefined ||
            value === "" ||
            value === null ||
            value === false;

        return isEmpty ? " duraction-300 focus-visible:ring-red-500/50 border-red-500 focus:border-red-500" : "";
    };

    return (
        <motion.div
            initial={animate ? { y: 100, opacity: 0 } : {}}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
        >

            <form id={id} onSubmit={handleSubmit} className={"flex  bg-primary-50/10 backdrop-blur-md  flex-col gap-4 " + className}>
                {props?.header}
                <div className="space-y-2">
                    {inputs.map((input, index) => (
                        <div
                            key={index}
                            className={"    w-full px-1 " + inputsDisplayType + " " + input.className}
                        >
                            {input.type !== "checkbox" && input.label && (
                                <Label
                                    htmlFor={`${input.name as string}`}
                                    className="mb-1 min-w-[150px]  ms-3 font-medium"
                                >
                                    {input.label}
                                </Label>
                            )}

                            {input.type === "select" ? (
                                <Select
                                    dir={direction}
                                    disabled={disabled}
                                    defaultValue={formData[input.name] + ""}
                                    value={((formData[input.name] + "") as string)}
                                    onValueChange={(value) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            [input.name]: value,
                                        }));
                                    }}
                                >
                                    <SelectTrigger
                                        className={`w-full ${getErrorClass(input)}`}
                                        id={String(input.name)}
                                    >
                                        {input.icon && <InputIcon className={input.icon} />}
                                        <SelectValue placeholder={gW("select")} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {input.options.map((opt) => (
                                            <SelectItem
                                                className={`hover:bg-gray-100 duration-150 ${formData[input.name] === String(opt.value) &&
                                                    " bg-gray-200 "
                                                    }`}
                                                key={opt.value + ""}
                                                value={String(opt.value)}
                                            >
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : input.type === "checkbox" ? (
                                <div className="flex items-center gap-2">
                                    <Input
                                        disabled={disabled}
                                        id={String(input.name)}
                                        name={String(input.name)}
                                        type="checkbox"
                                        checked={!!formData[input.name]}
                                        onChange={handleChange}
                                        className={`w-4 h-4 ${getErrorClass(input)}`}
                                    />
                                    {input.label && (
                                        <Label htmlFor={String(input.name)} className="cursor-pointer">
                                            {input.label}
                                        </Label>
                                    )}
                                </div>
                            ) : input.type === "date" ? (
                                <div className="relative">
                                    {input.icon && (
                                        <i
                                            className={`pi ${input.icon} absolute top-1/2 -translate-y-1/2 left-3`}
                                        />
                                    )}
                                    <Input
                                        disabled={disabled}
                                        // required={input.required}
                                        id={String(input.name)}
                                        name={String(input.name)}
                                        type="date"
                                        value={(formData[input.name] as any) ?? ""}
                                        onChange={handleChange}
                                        className={`border rounded px-3 py-2 ${input.icon && "pl-10"} ${getErrorClass(input)}`}
                                    />
                                </div>
                            ) : input.type === "file" ? (
                                <div className="space-y-2">
                                    <Input
                                        disabled={disabled}
                                        id={String(input.name)}
                                        name={String(input.name)}
                                        type="file"
                                        accept={input.accept || "image/*"}
                                        onChange={handleFileChange}
                                        className={getErrorClass(input)}
                                    />

                                    {/* Image preview */}
                                    {formData[input.name] && typeof formData[input.name] !== "string" && (
                                        <img
                                            src={URL.createObjectURL(formData[input.name] as File)}
                                            alt="preview"
                                            className="w-32 h-32 object-cover rounded-lg border"
                                        />
                                    )}

                                    {/* Existing image (edit mode) */}
                                    {typeof formData[input.name] === "string" && (
                                        <img
                                            src={formData[input.name] as string}
                                            alt="current"
                                            className="w-32 h-32 object-cover rounded-lg border"
                                        />
                                    )}
                                </div>) : (
                                <div className="relative w-full ">
                                    {input.icon && (
                                        <i
                                            className={`pi ${input.icon} absolute top-1/2 -translate-y-1/2 left-3`}
                                        />
                                    )}

                                    <Input
                                        disabled={disabled}
                                        // required={input.required}
                                        id={String(input.name)}
                                        name={String(input.name)}
                                        type={input.type}
                                        placeholder={input.placeholder}
                                        value={(formData[input.name] as any) ?? ""}
                                        onChange={handleChange}
                                        className={`border rounded px-3 py-2 ${input.icon && "pl-10"} ${getErrorClass(input)}`}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {props.submitTitle && (
                    <Button
                        disabled={disabled}
                        type="submit"
                        className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
                    >
                        {props.loading && <Loader />} {props.submitTitle}
                    </Button>
                )}

                {props.footer}
            </form>
        </motion.div>
    );
}


